import { NumberInput } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import type { ReturnCarrierReconciliationListItem } from "@msuite/utils/types";
import { FormProvider, useForm } from "react-hook-form";
import { client } from "@/hono";

/** Props Interface */
interface BookCarrierReturnModalProps extends ModalProps {
	returnOrderId: string;
	row: ReturnCarrierReconciliationListItem;
}

/** The one field a carrier booking needs. */
interface BookCarrierReturnFields {
	quantity: number;
}

/**
 * Book carriers back off the site.
 *
 * There is no shelf to choose, which is the whole difference from the article booking: a carrier
 * is a counted type rather than something located on a rack, so its balance is the yard's total
 * and `loadCarrierStockTransactions` is itself the ledger.
 *
 * The quantity opens on what is still out and is not bounded upwards — a site can hand back
 * pallets that went out on somebody else's job.
 */
export function BookCarrierReturnModal({
	isOpen,
	onClose,
	returnOrderId,
	row,
}: BookCarrierReturnModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const qc = useQueryClient();
	const methods = useForm<BookCarrierReturnFields>({
		defaultValues: {
			quantity: row.outstandingQuantity > 0 ? row.outstandingQuantity : 1,
		},
	});

	/** Functions */
	async function handleOnSubmit(json: BookCarrierReturnFields) {
		const response = await client.stock["return-orders"][":returnOrderId"][
			"carrier-bookings"
		].$post({
			param: { returnOrderId },
			json: { loadCarrierId: row.loadCarrierId, quantity: json.quantity },
		});
		await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate(["return-order", returnOrderId]) });
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={`${t("stock.return_book")} — ${row.loadCarrierName}`}
				isLoading={methods.formState.isLoading}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
			>
				<Modal.Body>
					<NumberInput
						label={t("stock.return_book_quantity")}
						control={methods.control}
						name="quantity"
						isRequired
					/>
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
